import {
    computed,
    inject,
    Injectable,
    signal,
    WritableSignal,
} from '@angular/core';
import { VoiceStore } from '@dxp/voice';
import { VoiceActions } from '@dxp/core/types';
import { USER_STORE, WORKITEMS_STORE } from '@dxp/core/stores';
import {
    ICallState,
    IContactDisplay,
    IContactSelected,
    IConversation,
    IUser,
    IWorkItem,
} from '@dxp/core/models';
import { VoiceApiService } from '@dxp/core/api';

@Injectable({
    providedIn: 'root',
})
export class VoiceService {
    readonly #workItemsStore = inject(WORKITEMS_STORE);
    readonly #userStore = inject(USER_STORE);
    readonly #voiceApiService = inject(VoiceApiService);
    readonly #voiceStore = inject(VoiceStore);

    constructor() {
        this.user = this.#userStore.user();
    }

    contacts: WritableSignal<IContactDisplay> = signal<IContactDisplay>({
        display: false,
        title: '',
        parent: '',
        hidden: false,
    });

    user!: IUser;
    workItemId = signal<string>('');
    private _enquiryCallStartTime!: Date | null;
    private _inCall = signal(false);
    selectedContact: WritableSignal<IContactSelected | null> = signal(null);
    panelHighlightSet = signal('main');
    callerHold = signal(false);
    private _contactCardSelected = signal<string>('');

    onHold = ['OnHold', 'Disconnected', 'Trying', 'Connected', 'AllOnHold'];
    enquiryOnHold = ['EnquiryOnHold', 'AllOnHold'];

    onEnquiryCallDisconnected() {
        const conversation = this.conversation();
        if (conversation) {
            const callState: ICallState = {
                conversationId: conversation.conversationId,
                callState: 'Resume',
            };
            this.#voiceApiService.changeCallState(callState);
        }
    }

    workItem = computed<IWorkItem | null>(() => {
        const workItemId = this.workItemId();
        if (!workItemId) return null;
        return (
            this.#workItemsStore
                .workItems()
                .find(item => item.workItemId === workItemId) || null
        );
    });

    conversation = computed<IConversation | undefined>(() => {
        const workItem = this.workItem();
        const conversations = workItem?.conversations ?? [];
        const foundConversation = conversations.find(
            c => c.conversationId === workItem?.primaryConversationId,
        );
        return foundConversation;
    });

    agentInitiatedToAgent = computed(() => {
        const workItem = this.workItem();
        const conversations = workItem?.conversations ?? [];
        return (
            conversations.length === 2 &&
            conversations.every(c => c.isInternalCall === true)
        );
    });

    opOnHold = computed(() => {
        const state = this.conversation()?.enquiryCallState;
        if (!state) {
            return false;
        }

        if (this.onHold.includes(state)) {
            return true;
        }

        return false;
    });

    panelHighlighted = computed(() => {
        const state = this.conversation()?.enquiryCallState;

        if (!state) {
            return this.panelHighlightSet();
        }

        if (this.onHold.includes(state)) {
            return '';
        }

        return 'main';
    });

    enquiryPanelHighlighted = computed(() => {
        const state = this.conversation()?.enquiryCallState;

        if (!state) {
            return '';
        }

        if (this.enquiryOnHold.includes(state)) {
            return 'enquiry';
        }

        return '';
    });

    enquiryCallConnected = computed(() => {
        return this.conversation()?.enquiryCallState === 'Connected';
    });

    enquiryCallStartTime = computed(() => {
        if (
            this.conversation()?.enquiryCallState === 'none' ||
            this.conversation()?.enquiryCallState === 'Disconnected'
        ) {
            this._enquiryCallStartTime = null;
        }
        if (this.conversation()?.enquiryCallState === 'Connected') {
            if (!this._enquiryCallStartTime) {
                this._enquiryCallStartTime = new Date();
            }
            return this._enquiryCallStartTime;
        }

        return null;
    });

    enquiryCallDisplayed = computed(() => {
        return (
            this.conversation()?.enquiryCallState === 'Connected' ||
            this.conversation()?.enquiryCallState === 'Ringing' ||
            this.conversation()?.enquiryCallState === 'Trying' ||
            this.conversation()?.enquiryCallState === 'EnquiryOnHold'
        );
    });

    setContacts(
        display: boolean,
        title: string,
        parent: string,
        action: VoiceActions | undefined,
        hidden: boolean,
    ) {
        this.contacts.set({ display, title, parent, action, hidden });
    }

    hideContacts() {
        this.contacts.set({ ...this.contacts(), hidden: true });
    }

    get inCall() {
        return this._inCall() && this.conversation() !== undefined;
    }

    set inCall(call: boolean) {
        this._inCall.set(call);
        if (!call) {
            this.setContacts(false, '', '', undefined, false);
        }
    }

    get displayContacts() {
        return this.contacts().display;
    }

    setWorkItem(workItemId: string) {
        this.workItemId.set(workItemId);

        /**
         * Creates a new entry in the voice store for a voice task
         */
        this.#voiceStore.createVoiceTask(workItemId);
    }

    contactSelected(contact: IContactSelected) {
        this.selectedContact.set(contact);
    }

    updateContacts(partialUpdate: Partial<IContactDisplay>) {
        this.contacts.update(current => ({ ...current, ...partialUpdate }));
    }

    getContactCardSelected() {
        return this._contactCardSelected();
    }

    setContactCardSelected(id: string) {
        this._contactCardSelected.set(id);
    }
}
